import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';

import { useIsSoldOut } from '@/hooks/useIsSoldOut';
import { useStockRemaining } from '@/hooks/useStockRemaining';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { MenuById, trpc } from '@/lib/trpc';
import { useCartStore } from '@/stores/cart-store';
import { usePickupDateTimeStore } from '@/stores/pickup-datetime-store';

import AddToCartButton from '../features/item/components/AddToCartButton';
import ItemDetailHeader from '../features/item/components/ItemDetailHeader';
import ItemInfo from '../features/item/components/ItemInfo';
import MenuImage from '../features/item/components/MenuImage';
import OptionSection from '../features/item/components/OptionSection';
import QuantitySelector from '../features/item/components/QuantitySelector';

import type { SelectedOption as OrderSelectedOption } from '@/types/cart';

type MenuOption = NonNullable<MenuById>['optionGroups'][number]['options'][number];
type MenuOptionGroupWithOptions = NonNullable<MenuById>['optionGroups'][number];

type SelectedOptionsMap = Record<string, MenuOption>;

// API 옵션을 UI 컴포넌트가 기대하는 형태로 변환
interface UIOption {
  id: string;
  name: string;
  price: number;
  required: boolean;
  disabled?: boolean;
}

function transformOptionsForUI(options: MenuOption[], disabledOptionIds: Set<number>): UIOption[] {
  return options.map(option => ({
    id: `option-${option.id}`,
    name:
      option.additionalPrice && option.additionalPrice > 0
        ? `${option.optionValue} (+${option.additionalPrice.toLocaleString()}원)`
        : option.optionValue,
    price: option.additionalPrice || 0,
    required: false, // 개별 옵션은 required가 아님, 그룹이 required
    disabled: disabledOptionIds.has(option.id),
  }));
}

// 유니크 아이템 ID 생성 함수
function generateUniqueItemId(baseId: number, selectedOptions: SelectedOptionsMap): string {
  const sortedKeys = Object.keys(selectedOptions).sort();
  const optionIds = sortedKeys.map(key => selectedOptions[key].id);
  return `${baseId}-${optionIds.join('-')}`;
}

export default function ItemDetailPage() {
  const params = useParams();
  const menuId = params.id ? parseInt(params.id) : 0;

  const navigate = useNavigate();
  // Fetch menu + option groups via tRPC
  const { data: menu, isLoading, isError, error } = useQuery(trpc.menu.byId.queryOptions({ id: menuId }));
  const addItem = useCartStore(state => state.addItem);
  const { pickupDate } = usePickupDateTimeStore();
  const { isSoldOut } = useIsSoldOut(pickupDate);
  const { menuStockMap, breadStockMap, isBreadMap, breadOptionsByMenu } = useStockRemaining(pickupDate);

  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsMap>({});
  const isInitialized = useRef(false);

  useScrollToTop();

  // 기본 옵션 설정
  useEffect(() => {
    if (menu && !isInitialized.current) {
      const defaultOptions: SelectedOptionsMap = {};

      // 각 옵션 그룹에서 기본값 설정
      menu.optionGroups.forEach((group: MenuOptionGroupWithOptions) => {
        const defaultOption = group.options.find(opt => opt.isDefaultChoice) || group.options[0];
        if (defaultOption) {
          defaultOptions[group.groupName] = defaultOption;
        }
      });

      setSelectedOptions(defaultOptions);
      isInitialized.current = true;
    }
  }, [menu]);

  useEffect(() => {
    if (!menu) {
      return;
    }

    let limit = menu.defaultPurchaseLimit || 0;
    Object.values(selectedOptions).forEach(option => {
      if (option.purchaseLimitOverride && option.purchaseLimitOverride > 0) {
        limit = Math.min(limit, option.purchaseLimitOverride);
      }
    });

    const hasHalfOption = Object.values(selectedOptions).some(option => option.optionValue.includes('하프'));
    const menuRemaining = menuStockMap.get(menu.id);
    if (menuRemaining !== undefined) {
      if (isBreadMap.get(menu.id)) {
        limit = Math.min(limit, Math.floor(menuRemaining / (hasHalfOption ? 0.5 : 1)));
      } else {
        limit = Math.min(limit, Math.floor(menuRemaining));
      }
    }

    const menuBreadOptions = breadOptionsByMenu[String(menu.id)] ?? [];
    const selectedBreadOption = Object.values(selectedOptions).find((option) =>
      menuBreadOptions.some((candidate) => candidate.optionId === option.id)
    );
    if (selectedBreadOption) {
      const breadOption = menuBreadOptions.find(
        (candidate) => candidate.optionId === selectedBreadOption.id
      );
      if (breadOption) {
        const breadRemaining = breadStockMap.get(breadOption.breadId);
        if (breadRemaining !== undefined) {
          limit = Math.min(limit, Math.floor(breadRemaining));
        }
      }
    }

    const nextLimit = Math.max(0, limit);
    if (quantity > nextLimit && nextLimit > 0) {
      setQuantity(nextLimit);
    }
  }, [breadOptionsByMenu, breadStockMap, isBreadMap, menu, menuStockMap, quantity, selectedOptions]);

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white w-full">
        <ItemDetailHeader />
        <div className="h-[50px]" />
        <div className="flex items-center justify-center min-h-[calc(100vh-50px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">상품 정보를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (isError || !menu) {
    return (
      <div className="min-h-screen bg-white w-full">
        <ItemDetailHeader />
        <div className="h-[50px]" />
        <div className="flex items-center justify-center min-h-[calc(100vh-50px)]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error?.message || '상품을 찾을 수 없습니다.'}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              뒤로 가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 현재 구매 제한 계산 함수
  const getCurrentPurchaseLimit = () => {
    let currentLimit = menu.defaultPurchaseLimit || 0;

    // 선택된 옵션들의 PurchaseLimitOverride 확인
    Object.values(selectedOptions).forEach(option => {
      if (option.purchaseLimitOverride && option.purchaseLimitOverride > 0) {
        currentLimit = Math.min(currentLimit, option.purchaseLimitOverride);
      }
    });

    return currentLimit;
  };

  const currentPurchaseLimit = getCurrentPurchaseLimit();
  const selectedOptionsIncludeHalf = Object.values(selectedOptions).some(option =>
    option.optionValue.includes('하프')
  );

  const getSelectedBreadOption = () => {
    const menuBreadOptions = breadOptionsByMenu[String(menu.id)] ?? [];

    for (const option of Object.values(selectedOptions)) {
      const matchedOption = menuBreadOptions.find((item) => item.optionId === option.id);
      if (matchedOption) {
        return matchedOption;
      }
    }

    return null;
  };

  const getStockAwarePurchaseLimit = () => {
    let limit = currentPurchaseLimit;

    const menuRemaining = menuStockMap.get(menu.id);
    if (menuRemaining !== undefined) {
      if (isBreadMap.get(menu.id)) {
        const unit = selectedOptionsIncludeHalf ? 0.5 : 1;
        limit = Math.min(limit, Math.floor(menuRemaining / unit));
      } else {
        limit = Math.min(limit, Math.floor(menuRemaining));
      }
    }

    const selectedBreadOption = getSelectedBreadOption();
    if (selectedBreadOption) {
      const breadRemaining = breadStockMap.get(selectedBreadOption.breadId);
      if (breadRemaining !== undefined) {
        limit = Math.min(limit, Math.floor(breadRemaining));
      }
    }

    return Math.max(0, limit);
  };

  const stockAwarePurchaseLimit = getStockAwarePurchaseLimit();
  const disabledOptionIds = new Set<number>();
  const menuRemaining = menuStockMap.get(menu.id);
  const menuBreadOptions = breadOptionsByMenu[String(menu.id)] ?? [];

  menu.optionGroups.forEach((group) => {
    const hasHalfWholeOptions = group.options.some(
      (option) => option.optionValue.includes('하프') || option.optionValue.includes('홀')
    );

    group.options.forEach((option) => {
      if (isBreadMap.get(menu.id) && hasHalfWholeOptions && menuRemaining !== undefined) {
        if (option.optionValue.includes('홀') && menuRemaining < 1) {
          disabledOptionIds.add(option.id);
        }
        if (option.optionValue.includes('하프') && menuRemaining < 0.5) {
          disabledOptionIds.add(option.id);
        }
      }

      const matchedBreadOption = menuBreadOptions.find((item) => item.optionId === option.id);
      if (matchedBreadOption && (breadStockMap.get(matchedBreadOption.breadId) ?? 0) <= 0) {
        disabledOptionIds.add(option.id);
      }
    });
  });

  // 품절 여부 확인
  const isItemSoldOut = menu.isSoldout || isSoldOut(menu.id) || stockAwarePurchaseLimit <= 0;

  // 옵션을 고려한 가격 계산
  const calculatePriceWithOptions = () => {
    const additionalPrice = Object.values(selectedOptions).reduce(
      (total, option) => total + (option.additionalPrice || 0),
      0
    );
    return menu.price + additionalPrice;
  };

  // 총 가격 계산
  const calculateTotalPrice = () => {
    return calculatePriceWithOptions() * quantity;
  };

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(Math.max(1, Math.min(stockAwarePurchaseLimit, newQuantity)));
  };

  const handleOptionChange = (groupName: string, optionId: string) => {
    const group = menu.optionGroups.find(g => g.groupName === groupName);
    if (!group) return;

    const option = group.options.find(opt => `option-${opt.id}` === optionId);
    if (!option || disabledOptionIds.has(option.id)) return;

    setSelectedOptions(prev => {
      return { ...prev, [groupName]: option };
    });
  };

  const handleAddToCart = () => {
    // 품절 체크
    if (isItemSoldOut) {
      toast.error('품절된 상품입니다.');
      return;
    }

    // 필수 옵션 검증
    // 필수 옵션 검증 (그룹 단위 required 체크)
    const missingGroups: string[] = [];
    menu.optionGroups.forEach(group => {
      if (group.isRequired && !selectedOptions[group.groupName]) {
        missingGroups.push(group.groupName);
      }
    });
    const validation = { isValid: missingGroups.length === 0, missingGroups };
    if (!validation.isValid) {
      toast.error(`필수 옵션을 선택해주세요: ${validation.missingGroups.join(', ')}`);
      return;
    }

    // 선택된 옵션들을 구조화된 형태와 표시용 문자열로 모두 생성
    const optionTexts: string[] = [];
    const structuredSelectedOptions: OrderSelectedOption[] = Object.entries(selectedOptions).map(
      ([groupName, option]) => {
        optionTexts.push(
          option.additionalPrice && option.additionalPrice > 0
            ? `${option.optionValue} (+${option.additionalPrice.toLocaleString()}원)`
            : option.optionValue
        );
        return {
          group_id: option.groupId,
          group_name: groupName,
          option_id: option.id,
          option_value: option.optionValue,
          additional_price: option.additionalPrice || 0,
        } satisfies OrderSelectedOption;
      }
    );

    // 옵션 조합별로 유니크한 ID 생성
    const uniqueItemId = generateUniqueItemId(menu.id, selectedOptions);

    addItem({
      id: uniqueItemId,
      name: menu.name,
      price: calculatePriceWithOptions(),
      quantity: quantity,
      option: optionTexts.length > 0 ? optionTexts.join(', ') : undefined,
      selectedOptions: structuredSelectedOptions,
      image: menu.isImageVisible && menu.imageUrl ? menu.imageUrl : undefined,
      description: menu.description || undefined,
      purchaseLimit: currentPurchaseLimit,
    });

    toast.success('장바구니에 추가되었습니다.');
    navigate(-1);
  };

  const hasImage = menu.isImageVisible && menu.imageUrl;

  return (
    <div className="min-h-screen bg-white w-full">
      {/* Header */}
      <ItemDetailHeader hasImage={!!hasImage} />

      {/* Header spacer when no image */}
      {!hasImage && <div className="h-[50px]" />}

      {/* Menu Image - 이미지가 있을 때만 표시 */}
      {hasImage && menu.imageUrl && <MenuImage imageUrl={menu.imageUrl} menuName={menu.name} />}

      {/* Item Info */}
      <ItemInfo
        name={menu.name}
        description={menu.description || ''}
        totalPrice={calculateTotalPrice()}
        isSoldOut={isItemSoldOut}
        isNew={menu.isNew || false}
      />

      {/* Quantity Selector */}
      <QuantitySelector
        quantity={quantity}
        maxQuantity={stockAwarePurchaseLimit}
        onQuantityChange={handleQuantityChange}
      />

      <section className="px-5 pb-6">
        <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-md">
          <img src="/icons/warning.svg" alt="warning" width={18} height={18} />
          <div className="text-xs font-semibold text-gray-500">
            최대 수량 이상으로 구매를 원하실 경우 DM 문의가 필요해요.
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-1.5 bg-gray-100" />

      {/* 옵션 그룹들 */}
      {menu.optionGroups.map((group, index) => (
        <div key={group.groupName}>
          <OptionSection
            title={group.groupName}
            required={group.isRequired || false}
            options={transformOptionsForUI(group.options, disabledOptionIds)}
            selectedOption={
              selectedOptions[group.groupName] ? `option-${selectedOptions[group.groupName].id}` : undefined
            }
            onOptionChange={optionId => handleOptionChange(group.groupName, optionId)}
          />
          {index < menu.optionGroups.length - 1 && <div className="h-1.5 bg-gray-100" />}
        </div>
      ))}

      {/* Bottom spacer for fixed button */}
      <div className="h-[120px]" />

      {/* Add to Cart Button - Fixed */}
      <AddToCartButton totalPrice={calculateTotalPrice()} onAddToCart={handleAddToCart} disabled={isItemSoldOut} />
    </div>
  );
}
